import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { createPortal } from 'react-dom';
import {
  Send, Smile, Pin, Trash2, X, Image as ImageIcon,
  Paperclip, Reply, Edit2, Check, Star, CornerDownRight, FileText,
  Download, Users, Search, MoreVertical, Bot, Bell, Eye, EyeOff, UserMinus
} from 'lucide-react';
import { useMeeting, usePubSub } from '../../../config/sdkConfig';
import { useAgoraRTM } from '../../../context/AgoraRTMContext';
import { useAgoraChat } from '../../../context/AgoraChatContext';
import { useMeetingAppContext } from '../../../MeetingAppContextDef';
import { useSiteContext } from '../../../context/SiteContext';
import { useWhiteLabel } from '../../../context/WhiteLabelContext';
import { useMeetingState } from '../../../context/MeetingStateContext';
import { stringToNumericUid } from '../../../context/AgoraContext';
import AIChatAssistant from '../../sidebar/AIChatAssistant';
import ChatSummaryPanel from '../../sidebar/ChatSummaryPanel';
import NotificationSettingsModal from '../../sidebar/NotificationSettingsModal';

// Simple emoji picker data
const EMOJI_CATEGORIES = {
  'Smileys': ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '😉', '😍', '🥰', '😘'],
  'Gestures': ['👍', '👎', '👏', '🙌', '🤝', '✋', '🤚', '👋', '🤙', '💪', '🙏', '✌️', '🤞', '❤️'],
  'Objects': ['🎉', '🎊', '🔥', '⭐', '💯', '✅', '❌', '⚡', '💡', '📌', '🎯', '🚀', '💬', '📝'],
};

const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏', '👏', '🔥', '🎉', '✅'];

// Convert URLs in text to clickable links
const renderTextWithLinks = (text) => {
  const urlRegex =
    /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}(?:\/[^\s]*)?)/g;
  const parts = text.split(urlRegex);
  const testRegex =
    /^(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}(?:\/[^\s]*)?)$/;
  return parts.map((part, index) => {
    if (part && testRegex.test(part)) {
      let href = part;
      if (!part.startsWith('http://') && !part.startsWith('https://')) {
        href = `https://${part}`;
      }
      return (
        <a
          key={index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 hover:text-blue-300 underline break-all cursor-pointer"
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

// Generate random color based on name
function getColorFromName(name) {
  const colors = ['#00c853', '#6a1fbf', '#f59e0b', '#3b82f6', '#ef4444', '#10b981', '#8b5cf6', '#ec4899'];
  if (!name) return colors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name[0].toUpperCase();
}

const CHAT_DRAFT_STORAGE_KEY = 'rlink-studio-chat-draft';

// Format file size
function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export default function ChatPanel({ localParticipantName = 'You', transcriptHistory = [] }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState(() => {
    try {
      return sessionStorage.getItem(CHAT_DRAFT_STORAGE_KEY) || '';
    } catch {
      return '';
    }
  });
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [showPinned, setShowPinned] = useState(false);
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showNewMessagesIndicator, setShowNewMessagesIndicator] = useState(false);

  // Get shadow banned participants from context
  const { shadowBannedParticipants, setShadowBannedParticipants } = useMeetingAppContext();

  // Get meeting role and settings for permission checks and AI context
  const { meetingRole, meetingSettings } = useSiteContext();
  const { branding: wlBranding } = useWhiteLabel();

  // Meeting state for persisting reactions to backend and syncing on mount
  const { emitMessageReaction, emitChatMessage, emitDeleteMessage, chatHistory, socketChatMessages, meetingStartedAt, isConnected: socketConnected, requestChatHistorySync } = useMeetingState();

  // Belt-and-suspenders: every time ChatPanel mounts (incl. first open of
  // the sidebar), ask the server to re-emit the full chat history. The
  // response merges into chatHistory in MeetingStateContext (deduped by
  // id, sorted by timestamp), and the existing self-healing effect below
  // seeds local messages from chatHistory. Combined with display:none
  // mounting in StudioRightSidebar, this guarantees that the chat panel
  // always shows the complete meeting chat — even after panel reopen,
  // socket reconnect, tab change, or late join.
  useEffect(() => {
    if (typeof requestChatHistorySync === 'function') requestChatHistorySync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canModerateChat = ['moderator', 'co-host', 'speaker', 'host'].includes(meetingRole);
  // Stricter check: only host (moderator) and co-host for AI Assistant + Export visibility
  const isHostOrCoHost = meetingRole === 'moderator' || meetingRole === 'co-host';

  // PubSub for shadow ban — syncs with attendees panel
  const { publish: publishShadowBan } = usePubSub('SHADOW_BAN');
  // PubSub for kicking participants — same channel as attendees panel
  const { publish: publishKickParticipant } = usePubSub('KICK_PARTICIPANT');

  const handleKickParticipant = (participantId, displayName) => {
    publishKickParticipant(
      JSON.stringify({
        targetParticipantId: String(participantId),
        displayName: displayName || 'Participant',
        kickedBy: localParticipantName || 'Host',
      }),
      { persist: false }
    );
  };

  const handleToggleShadowBan = (participantId, displayName) => {
    const strId = String(participantId);
    // Also compute numeric Agora UID so attendees panel (which uses numeric UIDs) stays in sync
    const numericUid = String(stringToNumericUid(strId));
    const isCurrentlyBanned = shadowBannedParticipants?.has?.(participantId)
      || shadowBannedParticipants?.has?.(strId)
      || (numericUid && numericUid !== '0' && shadowBannedParticipants?.has?.(numericUid));
    const action = isCurrentlyBanned ? 'unban' : 'ban';

    setShadowBannedParticipants(prev => {
      const newSet = new Set(prev);
      if (isCurrentlyBanned) {
        newSet.delete(participantId);
        newSet.delete(strId);
        if (numericUid && numericUid !== '0') newSet.delete(numericUid);
      } else {
        newSet.add(strId);
        if (numericUid && numericUid !== '0') newSet.add(numericUid);
      }
      return newSet;
    });

    // Publish string ID — the PubSub listener in MeetingContainerNew will add it
    publishShadowBan(
      JSON.stringify({
        participantId: strId,
        displayName: displayName || 'Participant',
        action
      }),
      { persist: true }
    );
    // Also publish numeric UID so attendees panel picks it up
    if (numericUid && numericUid !== '0') {
      publishShadowBan(
        JSON.stringify({
          participantId: numericUid,
          displayName: displayName || 'Participant',
          action
        }),
        { persist: true }
      );
    }
  };

  // Starred message overlay state (shows for 10 seconds to all participants)
  const [starredMessage, setStarredMessage] = useState(null);
  const starredTimeoutRef = useRef(null);

  // Use ref to always have current shadow banned participants in callbacks
  // This fixes the stale closure issue in onMessageReceived
  const shadowBannedRef = useRef(shadowBannedParticipants);
  useEffect(() => {
    shadowBannedRef.current = shadowBannedParticipants;
  }, [shadowBannedParticipants]);

  // When a new shadow ban is applied, retroactively mark existing messages
  // from that user so they stay hidden even after the ban is removed
  const prevBannedRef = useRef(new Set());
  useEffect(() => {
    if (!shadowBannedParticipants || shadowBannedParticipants.size === 0) {
      prevBannedRef.current = new Set();
      return;
    }
    // Find newly banned IDs (in current but not in previous)
    const newlyBanned = [...shadowBannedParticipants].filter(id => !prevBannedRef.current.has(id));
    prevBannedRef.current = new Set(shadowBannedParticipants);

    if (newlyBanned.length === 0) return;

    // Mark all existing messages from newly banned users
    setMessages(prev => {
      let changed = false;
      const updated = prev.map(m => {
        if (m.isOwn || m.shadowBanned) return m;
        // Check if this message's sender matches any newly banned ID
        const senderId = m.participantId;
        const senderNumericUid = String(stringToNumericUid(senderId));
        const isBanned = newlyBanned.some(bannedId =>
          bannedId === senderId ||
          bannedId === String(senderId) ||
          bannedId === senderNumericUid
        );
        if (isBanned) {
          changed = true;
          return { ...m, shadowBanned: true };
        }
        return m;
      });
      return changed ? updated : prev;
    });
  }, [shadowBannedParticipants]);


  // Channel/DM states
  const [activeChannel, setActiveChannel] = useState('Everyone');
  const [showNewChatPopover, setShowNewChatPopover] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [lastReadPerChannel, setLastReadPerChannel] = useState({});

  // Image/File upload states
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [attachedFile, setAttachedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Reply & Edit states
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingText, setEditingText] = useState('');

  // Featured comment state
  const [featuredCommentId, setFeaturedCommentId] = useState(null);

  // AI Assistant, Summary, and Notification states
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    newMessages: true,
    privateOnly: false,
    mentions: true,
    urgentQueries: true,
    sound: true,
    desktop: true,
  });

  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const newChatRef = useRef(null);

  const mMeeting = useMeeting();
  const localParticipantId = mMeeting?.localParticipant?.id;

  // Get all participants from the meeting
  const participants = mMeeting?.participants ? [...mMeeting.participants.values()] : [];
  const otherParticipants = participants.filter(p => p.id !== localParticipantId);

  // Filter participants by search
  const filteredParticipants = otherParticipants.filter(p =>
    p.displayName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get unique channels from messages (private conversations)
  const privateChannels = [...new Set(messages.filter(m => m.channel && typeof m.channel === 'string' && m.channel !== 'Everyone').map(m => m.channel))];

  // Auto-focus chat input when panel mounts (e.g. switching back to chat tab)
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  // Auto-load waiting room carry-over messages for admitted users on mount
  // When host switches to Chat tab, this reads saved carry-over data and creates private channel tabs
  useEffect(() => {
    try {
      const carryOverData = sessionStorage.getItem('waitingRoomChatCarryOver');
      if (!carryOverData) return;
      const allCarryOver = JSON.parse(carryOverData);
      const names = Object.keys(allCarryOver);
      if (names.length === 0) return;

      const allWrMessages = [];
      for (const name of names) {
        const chatHistory = allCarryOver[name];
        if (!Array.isArray(chatHistory) || chatHistory.length === 0) continue;
        const wrMessages = chatHistory.map((msg) => ({
          id: msg.id,
          message: msg.message || '',
          user: msg.senderName || 'Unknown',
          userId: msg.senderId || 'waiting-room-host',
          participantId: msg.senderId || 'waiting-room-host',
          timestamp: new Date(msg.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          rawTimestamp: new Date(msg.sentAt).getTime(),
          isOwn: msg.senderRole === 'host',
          isPrivate: true,
          channel: name,
          isWaitingRoomMessage: true,
          reactions: [],
        }));
        allWrMessages.push(...wrMessages);
      }

      if (allWrMessages.length > 0) {
        setMessages((prev) => {
          const existingIds = new Set(prev.map(m => m.id));
          const newMsgs = allWrMessages.filter(m => !existingIds.has(m.id));
          return newMsgs.length > 0 ? [...newMsgs, ...prev] : prev;
        });
      }

      // Clean up after loading
      sessionStorage.removeItem('waitingRoomChatCarryOver');
      sessionStorage.removeItem('waitingRoomCarryOverToggles');
    } catch (e) {
      console.warn('[ChatPanel] Failed to auto-load waiting room carry-over:', e);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper function to check if sender is shadow banned
  // Uses ref to always get current value (fixes stale closure in callbacks)
  // Checks multiple ID formats because:
  //   - Shadow ban Set stores numeric Agora UIDs (from participant list)
  //   - Agora Chat messages use original string userIds (e.g. MongoDB ObjectId)
  // So we check: original ID, String(ID), and stringToNumericUid(ID) conversion
  const isShadowBanned = (senderId) => {
    const currentBanned = shadowBannedRef.current;
    if (!currentBanned || !currentBanned.has || !senderId) {
      return false;
    }
    // Check original ID and string version
    if (currentBanned.has(senderId) || currentBanned.has(String(senderId))) {
      return true;
    }
    // Check numeric UID conversion (shadow ban stores numeric UIDs from participant list,
    // but Agora Chat messages use original string userIds)
    const numericUid = String(stringToNumericUid(senderId));
    if (numericUid && numericUid !== '0' && currentBanned.has(numericUid)) {
      return true;
    }
    return false;
  };

  // Filter messages by active channel and shadow ban status
  // Shadow banned messages are hidden from everyone EXCEPT the sender
  const filteredMessages = messages.filter(m => {
    // Filter by channel
    const channelMatch = activeChannel === 'Everyone'
      ? (!m.channel || m.channel === 'Everyone')
      : m.channel === activeChannel;

    if (!channelMatch) return false;

    // Permanently hidden: messages tagged as sent during a shadow ban period
    // These stay hidden even after the ban is removed
    if (m.shadowBanned && !m.isOwn) {
      return false;
    }

    // Shadow ban filter: hide messages from currently banned users
    // EXCEPT if it's the user's own message (they should see their own messages)
    if (!m.isOwn && m.participantId !== localParticipantId) {
      if (isShadowBanned(m.participantId)) {
        return false;
      }
    }

    return true;
  });

  // Get unread count for a channel.  Uses a per-channel timestamp watermark
  // (`lastReadPerChannel[channel]` holds the rawTimestamp of the newest
  // message seen while that channel was active) because ID-based comparison
  // is fragile — if a message's ID gets regenerated or doesn't match due to
  // type coercion, findIndex returns -1 and the count never clears.  A
  // strictly-greater-than timestamp compare is robust against those issues.
  // Additionally: if the channel IS currently active, the count is always 0.
  const getUnreadCount = (channel) => {
    if (channel === activeChannel) return 0;
    const lastReadTs = lastReadPerChannel[channel];
    return messages.filter(m => {
      if (m.isOwn) return false;
      const inChannel = m.channel === channel || (!m.channel && channel === 'Everyone');
      if (!inChannel) return false;
      if (!lastReadTs) return true; // never viewed — everything is unread
      return (m.rawTimestamp || 0) > lastReadTs;
    }).length;
  };

  // Track which message IDs we've already processed to avoid duplicates
  const processedMessageIds = useRef(new Set());

  // Use Agora Chat for messages
  const {
    messages: chatMessages,
    sendMessage: chatSendMessage,
    sendPrivateMessage: chatSendPrivateMessage,
    isConnected: chatConnected,
    connectionFailed: chatConnectionFailed,
  } = useAgoraChat();

  // Chat works if EITHER Agora Chat OR Socket.IO is connected
  const canSendChat = chatConnected || socketConnected;

  // Use Agora RTM for commands, typing, presence (stays on RTM)
  const {
    sendCommand: rtmSendCommand,
    sendTypingIndicator,
    typingUsers,
    isLoggedIn: rtmConnected,
    isChannelReady,
    localUserName,
    localUserId,
    isRealContext,
    presenceSnapshot,
  } = useAgoraRTM();

  // Debug connection status
  useEffect(() => {
    console.log('[ChatPanel] Status:', {
      chatConnected,
      socketConnected,
      canSendChat,
      rtmConnected,
      isChannelReady,
      messageCount: chatMessages?.length || 0
    });
  }, [chatConnected, socketConnected, canSendChat, rtmConnected, isChannelReady, chatMessages?.length]);

  // Generate a consistent message ID that will be the same across all clients
  // Uses the sender's userId and timestamp as a stable key
  const generateConsistentMessageId = (userId, timestamp) => {
    return `msg-${userId}-${timestamp}`;
  };

  // Sync Agora Chat messages to local state with additional metadata
  useEffect(() => {
    if (chatMessages && chatMessages.length > 0) {
      console.log('[ChatPanel] Processing', chatMessages.length, 'chatMessages, processedIds size:', processedMessageIds.current.size);
      const processedMessages = chatMessages.map(msg => {
        // Generate a consistent ID that's the same for all clients
        // Ensure userId is a string for consistent ID generation
        const consistentId = generateConsistentMessageId(String(msg.userId), msg.timestamp);

        // Skip if already processed
        if (processedMessageIds.current.has(consistentId)) {
          return null;
        }
        console.log('[ChatPanel] New message:', { id: consistentId, from: msg.userName, text: msg.text?.substring(0, 30) });
        processedMessageIds.current.add(consistentId);

        // Tag messages from shadow banned users so they stay hidden permanently
        // (even after the ban is removed)
        const senderIsBanned = !msg.isLocal && isShadowBanned(msg.userId);

        // Determine the channel for private messages
        let channel = 'Everyone';
        if (msg.isPrivate) {
          // For incoming private messages, the channel is the sender's name
          // This allows filtering by sender in the UI
          channel = msg.userName || `User ${msg.userId}`;
          console.log('[ChatPanel] Received private message, channel:', channel);
        }

        // Convert reactions from backend format { '👍': ['u1','u2'] } to UI format [{ emoji, count, users }]
        let reactions = [];
        if (msg.reactions) {
          if (Array.isArray(msg.reactions)) {
            reactions = msg.reactions.map(r => ({
              emoji: r.emoji,
              count: r.count || 0,
              users: r.users || [],
            }));
          } else {
            reactions = Object.entries(msg.reactions).map(([emoji, users]) => ({
              emoji,
              count: Array.isArray(users) ? users.length : (users || 0),
              users: Array.isArray(users) ? users : [],
            }));
          }
        }

        return {
          // Use consistent ID so all clients have the same ID for the same message
          id: consistentId,
          participantId: msg.userId,
          user: msg.userName || 'Unknown',
          message: msg.text,
          timestamp: new Date(msg.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          }),
          rawTimestamp: msg.timestamp, // Keep raw timestamp for consistent ID generation
          isOwn: msg.isLocal,
          reactions,
          isPinned: msg.isPinned || false,
          // Include attachment data from RTM message
          image: msg.image || null,
          file: msg.file || null,
          replyTo: msg.replyTo || null,
          channel: channel,
          isPrivate: msg.isPrivate || false,
          shadowBanned: senderIsBanned, // Permanently tag messages sent during shadow ban
        };
      }).filter(Boolean);

      if (processedMessages.length > 0) {
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const uniqueNewMessages = processedMessages.filter(m => !existingIds.has(m.id));
          if (uniqueNewMessages.length === 0) return prev;
          // Always append new messages at the end (bottom of chat)
          // Only sort if needed to maintain chronological order within the same batch
          return [...prev, ...uniqueNewMessages];
        });
      }
    }
  }, [chatMessages, localParticipantId]);

  // Merge Socket.IO broadcast messages into local display
  // These arrive when Agora Chat delivery fails for a participant
  // but the sender's message was persisted and broadcast via Socket.IO
  useEffect(() => {
    if (!socketChatMessages || socketChatMessages.length === 0) return;

    setMessages(prev => {
      const existingIds = new Set(prev.map(m => m.id));
      const newFromSocket = socketChatMessages
        .filter(msg => !existingIds.has(msg.id) && !processedMessageIds.current.has(msg.id))
        .map(msg => {
          const consistentId = msg.id || generateConsistentMessageId(String(msg.userId), msg.timestamp);
          // Add to processedMessageIds to prevent Agora Chat from re-adding
          processedMessageIds.current.add(consistentId);
          return {
            id: consistentId,
            participantId: msg.userId,
            user: msg.userName || 'Unknown',
            message: msg.text,
            timestamp: new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            rawTimestamp: msg.timestamp,
            isOwn: false,
            reactions: [],
            isPinned: msg.isPinned || false,
            image: msg.image || null,
            file: msg.file || null,
            replyTo: msg.replyTo || null,
            channel: 'Everyone',
            isPrivate: false,
          };
        });

      if (newFromSocket.length === 0) return prev;
      return [...prev, ...newFromSocket];
    });
  }, [socketChatMessages]);

  // Seed/reconcile messages from the REST snapshot's chatHistory. This
  // covers pre-host chat (guests can send messages before the host arrives,
  // and the Agora Chat SDK history fetch may not include them) AND acts as
  // a self-healing path when local message state somehow gets cleared
  // mid-session (was previously gated on `messages.length === 0` AND
  // tracked seen IDs in a permanent ref, which made it impossible to
  // recover — once messages got cleared, every ID was "already processed"
  // so seeded was always empty and the chat stayed empty forever).
  //
  // New behavior: every time chatHistory updates we compute the set of
  // messages in chatHistory that aren't already in `messages` (by id) and
  // append them. Doesn't clobber live state because we dedupe before
  // adding, doesn't lock out recovery because we dedupe against current
  // messages — not a permanent processed-IDs ref.
  useEffect(() => {
    if (!chatHistory || chatHistory.length === 0) return;

    setMessages(prev => {
      const existingIds = new Set(prev.map(m => m.id));
      const toSeed = chatHistory
        .filter(msg => msg?.id && !existingIds.has(msg.id))
        .map(msg => {
          // Convert backend reactions format { '👍': ['u1','u2'] } to UI array format
          let reactions = [];
          if (msg.reactions) {
            if (Array.isArray(msg.reactions)) {
              reactions = msg.reactions.map(r => ({ emoji: r.emoji, count: r.count || 0, users: r.users || [] }));
            } else {
              reactions = Object.entries(msg.reactions).map(([emoji, users]) => ({
                emoji,
                count: Array.isArray(users) ? users.length : (users || 0),
                users: Array.isArray(users) ? users : [],
              }));
            }
          }
          return {
            id: msg.id,
            participantId: msg.userId,
            user: msg.userName || 'Unknown',
            message: msg.text,
            timestamp: new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            rawTimestamp: msg.timestamp,
            // Compare against local participant — chatHistory can include the
            // local user's own messages from a prior tab/session.
            isOwn: String(msg.userId) === String(localParticipantId),
            reactions,
            isPinned: msg.isPinned || false,
            image: msg.image || null,
            file: msg.file || null,
            replyTo: msg.replyTo || null,
            channel: 'Everyone',
            isPrivate: false,
          };
        });

      if (toSeed.length === 0) return prev;
      // Merge while preserving chronological order by rawTimestamp; keep
      // the existing order for items already in `prev` to avoid jitter.
      const combined = [...prev, ...toSeed];
      combined.sort((a, b) => (a.rawTimestamp || 0) - (b.rawTimestamp || 0));
      return combined;
    });
  }, [chatHistory, localParticipantId]);

  // Merge reactions from backend chatHistory into local messages
  // This ensures reactions persist when ChatPanel re-mounts (tab switch)
  useEffect(() => {
    if (!chatHistory || chatHistory.length === 0) return;

    // Build a map of messageId -> reactions from backend
    const reactionMap = new Map();
    chatHistory.forEach(msg => {
      if (msg.reactions && Object.keys(msg.reactions).length > 0) {
        // Backend format: { '👍': ['u1','u2'] } → UI format: [{ emoji, count }]
        const uiReactions = Array.isArray(msg.reactions)
          ? msg.reactions.map(r => ({ emoji: r.emoji, count: r.count || 0, users: r.users || [] }))
          : Object.entries(msg.reactions).map(([emoji, users]) => ({
              emoji,
              count: Array.isArray(users) ? users.length : (users || 0),
              users: Array.isArray(users) ? users : [],
            }));
        if (uiReactions.length > 0) {
          reactionMap.set(msg.id, uiReactions);
        }
      }
    });

    if (reactionMap.size === 0) return;

    setMessages(prev => {
      let changed = false;
      const updated = prev.map(m => {
        const backendReactions = reactionMap.get(m.id);
        if (backendReactions && (!m.reactions || m.reactions.length === 0)) {
          changed = true;
          return { ...m, reactions: backendReactions };
        }
        return m;
      });
      return changed ? updated : prev;
    });
  }, [chatHistory]);

  // PubSub for spotlight participant (broadcast to all participants via RTM command)
  const publishSpotlight = useCallback((payload) => {
    try {
      const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
      rtmSendCommand?.('SPOTLIGHT_PARTICIPANT', data);
    } catch (error) {
      console.error('Failed to publish spotlight:', error);
    }
  }, [rtmSendCommand]);

  // Track whether user is near the bottom of the chat scroll
  const isNearBottomRef = useRef(true);
  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 80;
    isNearBottomRef.current = nearBottom;
    if (nearBottom) {
      setShowNewMessagesIndicator(false);
    }
  }, []);

  // Scroll to bottom helper (used by new messages indicator click)
  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      setShowNewMessagesIndicator(false);
    }
  }, []);

  // Auto scroll to bottom only if user hasn't scrolled up
  const prevMessageCountRef = useRef(0);
  const prevChannelRef = useRef(activeChannel);
  useEffect(() => {
    if (!scrollRef.current) return;
    const newCount = filteredMessages.length;
    const isNewMessage = newCount > prevMessageCountRef.current;
    const isChannelSwitch = activeChannel !== prevChannelRef.current;
    // If the newest message is the local user's own, always auto-scroll
    // regardless of their current scroll position — their own send
    // shouldn't get buried behind a "New messages" indicator.
    const lastMsg = filteredMessages[filteredMessages.length - 1];
    const lastIsOwn = isNewMessage && lastMsg?.isOwn;
    prevMessageCountRef.current = newCount;
    prevChannelRef.current = activeChannel;

    // Scroll to bottom: on channel switch, own-message send, or new message
    // when already near bottom.  Use rAF so the browser has flushed the new
    // message's layout before we read scrollHeight — otherwise the scroll
    // can land one message short on slower devices.
    const shouldScroll = isChannelSwitch || lastIsOwn ||
      (isNewMessage && isNearBottomRef.current);
    if (shouldScroll) {
      const el = scrollRef.current;
      requestAnimationFrame(() => {
        if (el) el.scrollTop = el.scrollHeight;
      });
      // If the new message has an image, images load async and grow the
      // container after our initial scroll — re-scroll once the image
      // finishes loading so the message stays anchored at the bottom.
      if (lastMsg?.image) {
        const imgs = el.querySelectorAll('img');
        const lastImg = imgs[imgs.length - 1];
        if (lastImg && !lastImg.complete) {
          const rescroll = () => {
            if (el && (isNearBottomRef.current || lastIsOwn)) {
              el.scrollTop = el.scrollHeight;
            }
          };
          lastImg.addEventListener('load', rescroll, { once: true });
          lastImg.addEventListener('error', rescroll, { once: true });
        }
      }
    } else if (isNewMessage && !isNearBottomRef.current) {
      setShowNewMessagesIndicator(true);
    }
  }, [messages, activeChannel, filteredMessages]);

  // Mark messages in the active channel as read.  Runs in its own effect
  // (not gated on scrollRef) so the watermark is always updated when the
  // user switches channels or new messages arrive in the active channel —
  // even if the scroll container hasn't mounted yet on the first render.
  // Uses a timestamp watermark (not a message ID) so ID mismatches can't
  // cause the badge to stick.
  useEffect(() => {
    const lastMsg = filteredMessages[filteredMessages.length - 1];
    const ts = lastMsg?.rawTimestamp || Date.now();
    setLastReadPerChannel(prev => {
      const current = prev[activeChannel];
      // Always advance to the newest timestamp we've seen in this channel —
      // but don't go backwards if an older render somehow reprocesses.
      if (current != null && current >= ts) return prev;
      return { ...prev, [activeChannel]: ts };
    });
  }, [activeChannel, filteredMessages]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
        setShowEmojiPicker(false);
      }
    };
    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showEmojiPicker]);

  // Close new chat popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (newChatRef.current && !newChatRef.current.contains(e.target)) {
        setShowNewChatPopover(false);
      }
    };
    if (showNewChatPopover) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showNewChatPopover]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveMenuId(null);
    if (activeMenuId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeMenuId]);

  // Upload file to server (S3/DigitalOcean Spaces)
  const uploadFileToServer = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('meetingId', mMeeting?.meetingId || 'general');

      const baseUrl = process.env.REACT_APP_BASE_URL || '';
      const uploadUrl = `${baseUrl}/upload/chat`;
      console.log('[ChatPanel] Uploading file to:', uploadUrl);
      console.log('[ChatPanel] File details:', { name: file.name, size: file.size, type: file.type });

      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });

      console.log('[ChatPanel] Upload response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[ChatPanel] Upload HTTP error:', response.status, errorText);
        return { success: false, error: `HTTP ${response.status}: ${errorText}` };
      }

      const data = await response.json();
      console.log('[ChatPanel] Upload response data:', data);

      if (data.success) {
        console.log('[ChatPanel] File uploaded successfully:', data.url);
        return {
          success: true,
          url: data.url,
          fileName: data.fileName,
          fileSize: data.fileSize,
          fileType: data.fileType,
        };
      } else {
        console.error('[ChatPanel] File upload failed:', data.message);
        return { success: false, error: data.message };
      }
    } catch (error) {
      console.error('[ChatPanel] File upload error:', error);
      return { success: false, error: `Failed to upload file: ${error.message || error}` };
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim() && !imageFile && !attachedFile) return;
    if (!canSendChat) {
      console.warn('[ChatPanel] Neither Agora Chat nor Socket.IO connected');
      return;
    }

    setIsUploading(true);
    let imageUrl = null;
    let fileData = null;

    try {
      // Handle image upload to server
      if (imageFile) {
        console.log('[ChatPanel] Uploading image to server...');
        const uploadResult = await uploadFileToServer(imageFile);
        if (uploadResult.success) {
          imageUrl = uploadResult.url;
          console.log('[ChatPanel] Image uploaded successfully, URL:', imageUrl);
        } else {
          console.error('[ChatPanel] Image upload failed:', uploadResult.error);
          // Show error to user - don't use base64 fallback as it exceeds RTM message size limits
          alert(`Image upload failed: ${uploadResult.error}. Please try again.`);
          setIsUploading(false);
          return;
        }
      }

      // Handle file attachment upload to server
      if (attachedFile) {
        console.log('[ChatPanel] Uploading file to server...');
        const uploadResult = await uploadFileToServer(attachedFile);
        if (uploadResult.success) {
          fileData = {
            name: uploadResult.fileName || attachedFile.name,
            size: uploadResult.fileSize || attachedFile.size,
            type: uploadResult.fileType || attachedFile.type,
            url: uploadResult.url,
          };
          console.log('[ChatPanel] File uploaded successfully, URL:', uploadResult.url);
        } else {
          console.error('[ChatPanel] File upload failed:', uploadResult.error);
          // Show error to user
          alert(`File upload failed: ${uploadResult.error}. Please try again.`);
          setIsUploading(false);
          return;
        }
      }

      // Build message text - include attachment info for RTM
      let messageText = newMessage.trim();
      if (imageUrl && !messageText) {
        messageText = '[Image]';
      } else if (fileData && !messageText) {
        messageText = `[File: ${fileData.name}]`;
      }

      // Create message payload with attachments for RTM
      // We'll broadcast file/image URLs so all participants can download
      const attachmentPayload = {
        image: imageUrl || null,
        file: fileData || null,
        replyTo: replyingTo ? { id: replyingTo.id, user: replyingTo.user, message: replyingTo.message } : null,
      };

      let success = false;

      // Generate timestamp for consistent message ID
      // IMPORTANT: Use the RTM localUserId (not VideoSDK localParticipantId) for consistency
      // because receivers will see the RTM memberId, which is the RTM userId
      const msgTimestamp = Date.now();
      const rtmUserId = String(localUserId || localParticipantId);
      const consistentId = generateConsistentMessageId(rtmUserId, msgTimestamp);

      // Check if this is a private message (channel is not 'Everyone')
      if (activeChannel !== 'Everyone') {
        // Find the participant to get their ID for peer-to-peer messaging
        const targetParticipant = otherParticipants.find(p => p.displayName === activeChannel);
        if (targetParticipant) {
          // Agora RTC participants use numeric UIDs, but Agora Chat needs
          // the original string user ID used during chat registration.
          // Look up the original userId from RTM presence snapshot (keyed by original string IDs).
          let chatUserId = null;
          if (presenceSnapshot && presenceSnapshot.size > 0) {
            for (const [originalId, data] of presenceSnapshot.entries()) {
              if (data.states?.displayName === activeChannel) {
                chatUserId = originalId;
                break;
              }
            }
          }
          // Fallback: use the RTM localUserId format if presence lookup fails
          if (!chatUserId) {
            console.warn('[ChatPanel] Could not find original userId in presence snapshot for:', activeChannel, '- falling back to participant.id');
            chatUserId = targetParticipant.id;
          }

          console.log('[ChatPanel] Sending private message to:', activeChannel, 'Chat User ID:', chatUserId);
          success = await chatSendPrivateMessage?.(chatUserId, messageText);

          // Add to local messages for display
          if (success) {
            const msg = {
              id: consistentId,
              participantId: rtmUserId, // Use RTM userId for consistency with message IDs
              user: localParticipantName,
              message: messageText,
              timestamp: new Date(msgTimestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              rawTimestamp: msgTimestamp,
              isOwn: true,
              reactions: [],
              isPinned: false,
              image: imageUrl,
              file: fileData,
              replyTo: replyingTo ? { id: replyingTo.id, user: replyingTo.user, message: replyingTo.message } : null,
              channel: activeChannel,
              isPrivate: true,
            };
            setMessages(prev => [...prev, msg]);
          }
        } else {
          console.warn('[ChatPanel] Could not find participant for private message:', activeChannel);
        }
      } else {
        // Send to channel (group chat) with attachment data included in the message
        console.log('[ChatPanel] Sending message to channel with attachments:', {
          imageUrl,
          fileData,
          replyingTo,
          messageText,
          chatConnected,
          socketConnected,
        });

        // Try Agora Chat first (primary path)
        if (chatConnected) {
          const attachments = (imageUrl || fileData || replyingTo) ? {
            image: imageUrl,
            file: fileData,
            replyTo: replyingTo ? { id: replyingTo.id, user: replyingTo.user, message: replyingTo.message } : null,
          } : null;

          success = await chatSendMessage(messageText, attachments);
        }

        // Fallback: if Agora Chat failed or not connected, send via Socket.IO
        if (!success && socketConnected) {
          console.log('[ChatPanel] Agora Chat unavailable, sending via Socket.IO fallback');
          const fallbackMsg = {
            id: consistentId,
            text: messageText,
            userId: rtmUserId,
            userName: localParticipantName,
            timestamp: msgTimestamp,
            image: imageUrl || null,
            file: fileData || null,
            replyTo: replyingTo ? { id: replyingTo.id, user: replyingTo.user, message: replyingTo.message } : null,
          };
          emitChatMessage(fallbackMsg);
          // Add to local display
          setMessages(prev => [...prev, {
            id: consistentId,
            participantId: rtmUserId,
            user: localParticipantName,
            message: messageText,
            timestamp: new Date(msgTimestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            rawTimestamp: msgTimestamp,
            isOwn: true,
            reactions: [],
            isPinned: false,
            image: imageUrl,
            file: fileData,
            replyTo: replyingTo ? { id: replyingTo.id, user: replyingTo.user, message: replyingTo.message } : null,
            channel: 'Everyone',
            isPrivate: false,
          }]);
          success = true;
        }
      }

      if (success) {
        // Clear inputs on success
        setNewMessage('');
        try { sessionStorage.removeItem(CHAT_DRAFT_STORAGE_KEY); } catch {}
        setImagePreview(null);
        setImageFile(null);
        setAttachedFile(null);
        setReplyingTo(null);
        setShowEmojiPicker(false);
      } else {
        console.error('[ChatPanel] Failed to send message via RTM');
      }
    } catch (error) {
      console.error('[ChatPanel] Failed to send message:', error);
    } finally {
      setIsUploading(false);
      // Focus after React re-renders and removes disabled attribute
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle input change with typing indicator
  const handleInputChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    try {
      if (value) sessionStorage.setItem(CHAT_DRAFT_STORAGE_KEY, value);
      else sessionStorage.removeItem(CHAT_DRAFT_STORAGE_KEY);
    } catch {}
    sendTypingIndicator?.();
  };

  const addEmoji = (emoji) => {
    setNewMessage(prev => {
      const next = prev + emoji;
      try { sessionStorage.setItem(CHAT_DRAFT_STORAGE_KEY, next); } catch {}
      return next;
    });
    inputRef.current?.focus();
  };

  // Image handling
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setImageFile(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  // File handling
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAttachedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setAttachedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Drag-and-drop file handling
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    if (file.type.startsWith('image/')) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setAttachedFile(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
  });

  // Reply handling
  const handleReply = (msg) => {
    setReplyingTo(msg);
    inputRef.current?.focus();
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  // Edit handling
  const handleEditMessage = (msg) => {
    setEditingMessageId(msg.id);
    setEditingText(msg.message);
  };

  const handleSaveEdit = (msgId) => {
    if (!editingText.trim()) return;
    setMessages(prev => prev.map(m =>
      m.id === msgId ? { ...m, message: editingText, edited: true } : m
    ));
    setEditingMessageId(null);
    setEditingText('');
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditingText('');
  };

  // Pin handling - broadcasts to all participants
  const togglePin = (msgId) => {
    const msg = filteredMessages.find(m => m.id === msgId);
    if (!msg) return;

    const newPinned = !msg.isPinned;

    // Update local state
    setMessages(prev => prev.map(m => {
      if (m.id === msgId) {
        if (newPinned) {
          setPinnedMessages(pins => {
            if (pins.some(p => p.id === msgId)) return pins;
            return [...pins, { ...m, isPinned: true }];
          });
        } else {
          setPinnedMessages(pins => pins.filter(p => p.id !== msgId));
        }
        return { ...m, isPinned: newPinned };
      }
      return m;
    }));
    setActiveMenuId(null);

    // Broadcast pin state to all participants via RTM
    rtmSendCommand?.('CHAT_PIN', {
      messageId: msgId,
      isPinned: newPinned,
      message: newPinned ? {
        id: msg.id,
        user: msg.user,
        message: msg.message,
        timestamp: msg.timestamp,
        image: msg.image,
        file: msg.file,
      } : null,
      pinnedBy: localParticipantName,
    });
  };

  // Star message - broadcasts overlay to all participants for 10 seconds
  // Only hosts/presenters can star messages
  const handleStarMessage = (msgId) => {
    if (!canModerateChat) return;

    const msg = filteredMessages.find(m => m.id === msgId);
    if (!msg) return;

    // Clear any existing timeout
    if (starredTimeoutRef.current) {
      clearTimeout(starredTimeoutRef.current);
    }

    // Set local starred message state
    setStarredMessage(msg);
    setActiveMenuId(null);

    // Broadcast starred message overlay to all participants via RTM
    rtmSendCommand?.('STARRED_MESSAGE', {
      action: 'show',
      message: {
        id: msg.id,
        text: msg.message,
        user: msg.user,
        timestamp: msg.timestamp,
      }
    });

    // Clear after 10 seconds
    starredTimeoutRef.current = setTimeout(() => {
      setStarredMessage(null);
      rtmSendCommand?.('STARRED_MESSAGE', { action: 'hide' });
    }, 10000);
  };

  // Listen for starred message commands from other users
  const { subscribeToCommand } = useAgoraRTM();
  useEffect(() => {
    if (!subscribeToCommand) return;

    const unsubscribe = subscribeToCommand('STARRED_MESSAGE', (data) => {
      if (data.message?.action === 'show' && data.message?.message) {
        // Clear any existing timeout
        if (starredTimeoutRef.current) {
          clearTimeout(starredTimeoutRef.current);
        }
        setStarredMessage(data.message.message);
        // Auto-hide after 10 seconds
        starredTimeoutRef.current = setTimeout(() => {
          setStarredMessage(null);
        }, 10000);
      } else if (data.message?.action === 'hide') {
        setStarredMessage(null);
        if (starredTimeoutRef.current) {
          clearTimeout(starredTimeoutRef.current);
        }
      }
    });

    return () => unsubscribe?.();
  }, [subscribeToCommand]);

  // Feature/Spotlight comment handling - spotlights the message sender's video
  const toggleFeature = (msgId) => {
    const msg = filteredMessages.find(m => m.id === msgId);
    const isCurrentlyFeatured = featuredCommentId === msgId;

    // Toggle visual feature state (yellow star in chat)
    setFeaturedCommentId(isCurrentlyFeatured ? null : msgId);
    setActiveMenuId(null);

    // Broadcast spotlight to all participants via RTM
    if (msg?.participantId) {
      publishSpotlight({
        participantId: msg.participantId,
        displayName: msg.user,
        action: isCurrentlyFeatured ? 'unspotlight' : 'spotlight'
      });
    }
  };

  // Reaction handling - one reaction per user per message, toggles on re-click
  const addReaction = (msgId, emoji) => {
    const rtmUserId = String(localUserId || localParticipantId);
    const currentMsg = messages.find(m => m.id === msgId);
    if (!currentMsg) return;

    // Find if user already reacted with this emoji (toggle off) or a different emoji (switch)
    const clickedReaction = currentMsg.reactions.find(r => r.emoji === emoji);
    const alreadyReactedSame = clickedReaction?.users?.includes(rtmUserId);
    const previousReaction = currentMsg.reactions.find(r => r.emoji !== emoji && r.users?.includes(rtmUserId));

    // If user already has a different reaction on this message, remove it first
    if (previousReaction) {
      const oldEmoji = previousReaction.emoji;
      // Remove old reaction locally
      setMessages(prev => prev.map(m => {
        if (m.id !== msgId) return m;
        const updated = m.reactions.map(r => {
          if (r.emoji !== oldEmoji) return r;
          const newUsers = (r.users || []).filter(id => id !== rtmUserId);
          return { ...r, count: Math.max(0, r.count - 1), users: newUsers };
        }).filter(r => r.count > 0);
        return { ...m, reactions: updated };
      }));
      // Broadcast + persist old reaction removal
      rtmSendCommand?.('CHAT_REACTION', {
        messageId: msgId, emoji: oldEmoji, userName: localParticipantName,
        action: 'remove', userId: rtmUserId,
      });
      emitMessageReaction?.(msgId, oldEmoji, rtmUserId, 'remove');
    }

    // If clicking the same emoji they already have, just remove (toggle off) — already handled above if previousReaction
    if (alreadyReactedSame) {
      // Toggle off: remove this reaction
      setMessages(prev => prev.map(m => {
        if (m.id !== msgId) return m;
        const updated = m.reactions.map(r => {
          if (r.emoji !== emoji) return r;
          const newUsers = (r.users || []).filter(id => id !== rtmUserId);
          return { ...r, count: Math.max(0, r.count - 1), users: newUsers };
        }).filter(r => r.count > 0);
        return { ...m, reactions: updated };
      }));
      rtmSendCommand?.('CHAT_REACTION', {
        messageId: msgId, emoji, userName: localParticipantName,
        action: 'remove', userId: rtmUserId,
      });
      emitMessageReaction?.(msgId, emoji, rtmUserId, 'remove');
      return;
    }

    // Add the new reaction
    setMessages(prev => prev.map(m => {
      if (m.id !== msgId) return m;
      const existing = m.reactions.find(r => r.emoji === emoji);
      if (existing) {
        return {
          ...m,
          reactions: m.reactions.map(r =>
            r.emoji === emoji ? { ...r, count: r.count + 1, users: [...(r.users || []), rtmUserId] } : r
          )
        };
      }
      return { ...m, reactions: [...m.reactions, { emoji, count: 1, users: [rtmUserId] }] };
    }));
    rtmSendCommand?.('CHAT_REACTION', {
      messageId: msgId, emoji, userName: localParticipantName,
      action: 'add', userId: rtmUserId,
    });
    emitMessageReaction?.(msgId, emoji, rtmUserId, 'add');
  };

  // Delete handling - broadcasts to all participants AND persists to backend
  // so the message is removed from the recording chat file (S3) too.
  const deleteMessage = (msgId) => {
    // Update local state
    setMessages(prev => prev.filter(m => m.id !== msgId));
    setPinnedMessages(prev => prev.filter(p => p.id !== msgId));
    setActiveMenuId(null);

    // Broadcast delete to all participants via RTM (real-time UI sync)
    rtmSendCommand?.('CHAT_DELETE', {
      messageId: msgId,
      deletedBy: localParticipantName,
    });

    // Persist deletion server-side: removes from chat buffer + S3 chat files
    // (session file and any recording snapshots already written), so deletes
    // are reflected in recording details and shared replay links.
    emitDeleteMessage?.(msgId);
  };

  // Listen for reaction, delete, and pin commands from other users
  useEffect(() => {
    if (!subscribeToCommand) return;

    // Handle reactions from other users
    const unsubReaction = subscribeToCommand('CHAT_REACTION', (data) => {
      const { messageId, emoji, action = 'add', userId } = data.message || {};
      if (!messageId || !emoji) return;

      setMessages(prev => prev.map(m => {
        if (m.id !== messageId) return m;
        if (action === 'remove') {
          const updated = m.reactions.map(r => {
            if (r.emoji !== emoji) return r;
            const newUsers = (r.users || []).filter(id => id !== userId);
            return { ...r, count: Math.max(0, r.count - 1), users: newUsers };
          }).filter(r => r.count > 0);
          return { ...m, reactions: updated };
        }
        // action === 'add'
        const existing = m.reactions.find(r => r.emoji === emoji);
        if (existing) {
          if (userId && existing.users?.includes(userId)) return m; // already reacted
          return {
            ...m,
            reactions: m.reactions.map(r =>
              r.emoji === emoji ? { ...r, count: r.count + 1, users: [...(r.users || []), ...(userId ? [userId] : [])] } : r
            )
          };
        }
        return { ...m, reactions: [...m.reactions, { emoji, count: 1, users: userId ? [userId] : [] }] };
      }));
    });

    // Handle deletes from other users (hosts/presenters)
    const unsubDelete = subscribeToCommand('CHAT_DELETE', (data) => {
      console.log('[ChatPanel] Received CHAT_DELETE command:', data);
      const { messageId, deletedBy } = data.message || {};
      if (!messageId) {
        console.log('[ChatPanel] Invalid delete data, missing messageId');
        return;
      }

      console.log(`[ChatPanel] Deleting message ${messageId} (deleted by ${deletedBy})`);
      setMessages(prev => {
        const beforeCount = prev.length;
        const filtered = prev.filter(m => m.id !== messageId);
        console.log(`[ChatPanel] Messages before delete: ${beforeCount}, after: ${filtered.length}`);
        return filtered;
      });
      setPinnedMessages(prev => prev.filter(p => p.id !== messageId));
    });

    // Handle pin commands from other users
    const unsubPin = subscribeToCommand('CHAT_PIN', (data) => {
      console.log('[ChatPanel] Received CHAT_PIN command:', data);
      const { messageId, isPinned, message: pinnedMsg } = data.message || {};
      if (!messageId) return;

      setMessages(prev => prev.map(m => {
        if (m.id === messageId) {
          return { ...m, isPinned: isPinned };
        }
        return m;
      }));

      if (isPinned && pinnedMsg) {
        setPinnedMessages(prev => {
          // Don't add if already pinned
          if (prev.some(p => p.id === messageId)) return prev;
          return [...prev, { ...pinnedMsg, isPinned: true }];
        });
      } else {
        setPinnedMessages(prev => prev.filter(p => p.id !== messageId));
      }
    });

    // Handle attachment commands from other users (images/files)
    const unsubAttachment = subscribeToCommand('CHAT_ATTACHMENT', (data) => {
      console.log('[ChatPanel] Received CHAT_ATTACHMENT command:', data);
      const { messageId, senderId, senderName, image, file, replyTo } = data.message || {};
      if (!messageId) return;

      // Update the message with attachment data
      setMessages(prev => {
        // First check if the message exists and needs updating
        const existingMsg = prev.find(m => m.id === messageId);
        if (existingMsg) {
          // Update existing message with attachments
          return prev.map(m => {
            if (m.id === messageId) {
              return {
                ...m,
                image: image || m.image,
                file: file || m.file,
                replyTo: replyTo || m.replyTo,
              };
            }
            return m;
          });
        }
        // If message doesn't exist yet, it will be added when the RTM message arrives
        // Store attachment data to be merged when message arrives
        return prev;
      });
    });

    return () => {
      unsubReaction?.();
      unsubDelete?.();
      unsubPin?.();
      unsubAttachment?.();
    };
  }, [subscribeToCommand]);

  // Export chat functionality
  const exportChat = (format = 'txt') => {
    if (messages.length === 0) return;

    const meetingId = mMeeting?.meetingId || 'meeting';
    const date = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\//g, '-');
    const time = new Date().toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).replace(':', '-');

    let content = '';
    let filename = '';
    let mimeType = '';

    if (format === 'txt') {
      // Plain text format
      content = `${wlBranding.companyName || 'R-Link Studio'} Chat Export\n`;
      content += `Meeting ID: ${meetingId}\n`;
      content += `Exported: ${new Date().toLocaleString()}\n`;
      content += `Total Messages: ${messages.length}\n`;
      content += `${'='.repeat(50)}\n\n`;

      messages.forEach(msg => {
        content += `[${msg.timestamp}] ${msg.user}:\n`;
        if (msg.replyTo) {
          content += `  ↳ Replying to ${msg.replyTo.user}: "${msg.replyTo.message || '[Media]'}"\n`;
        }
        if (msg.image) {
          content += `  [Image attached]\n`;
        }
        if (msg.file) {
          content += `  [File: ${msg.file.name} (${formatFileSize(msg.file.size)})]\n`;
        }
        if (msg.message) {
          content += `  ${msg.message}\n`;
        }
        if (msg.reactions && msg.reactions.length > 0) {
          content += `  Reactions: ${msg.reactions.map(r => `${r.emoji}(${r.count})`).join(' ')}\n`;
        }
        if (msg.isPinned) {
          content += `  📌 Pinned\n`;
        }
        content += `\n`;
      });

      filename = `chat-export-${date}-${time}.txt`;
      mimeType = 'text/plain';
    } else if (format === 'json') {
      // JSON format for data export
      const exportData = {
        meetingId,
        exportedAt: new Date().toISOString(),
        totalMessages: messages.length,
        messages: messages.map(msg => ({
          id: msg.id,
          user: msg.user,
          participantId: msg.participantId,
          message: msg.message,
          timestamp: msg.timestamp,
          hasImage: !!msg.image,
          file: msg.file ? { name: msg.file.name, size: msg.file.size } : null,
          replyTo: msg.replyTo,
          reactions: msg.reactions,
          isPinned: msg.isPinned,
          edited: msg.edited || false
        }))
      };
      content = JSON.stringify(exportData, null, 2);
      filename = `chat-export-${date}-${time}.json`;
      mimeType = 'application/json';
    } else if (format === 'csv') {
      // CSV format
      content = 'Timestamp,User,Message,Has Image,File Name,Is Pinned,Reactions\n';
      messages.forEach(msg => {
        const escapedMessage = (msg.message || '').replace(/"/g, '""').replace(/\n/g, ' ');
        const reactions = msg.reactions.map(r => `${r.emoji}(${r.count})`).join(' ');
        content += `"${msg.timestamp}","${msg.user}","${escapedMessage}","${msg.image ? 'Yes' : 'No'}","${msg.file?.name || ''}","${msg.isPinned ? 'Yes' : 'No'}","${reactions}"\n`;
      });
      filename = `chat-export-${date}-${time}.csv`;
      mimeType = 'text/csv';
    }

    // Create and download the file
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showExportMenu]);

  // Start new private chat
  const startPrivateChat = (participant) => {
    const name = participant.displayName || participant.id;
    if (!name) return;
    setActiveChannel(name);
    setShowNewChatPopover(false);
    setSearchQuery('');

    // Load waiting room chat history if available (carry-over feature)
    try {
      const carryOverData = sessionStorage.getItem('waitingRoomChatCarryOver');
      if (carryOverData) {
        const allCarryOver = JSON.parse(carryOverData);
        if (allCarryOver[name] && Array.isArray(allCarryOver[name]) && allCarryOver[name].length > 0) {
          const wrMessages = allCarryOver[name].map((msg) => ({
            id: msg.id,
            message: msg.message || '',
            user: msg.senderName || 'Unknown',
            userId: msg.senderId || 'waiting-room-host',
            participantId: msg.senderId || 'waiting-room-host',
            timestamp: new Date(msg.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            rawTimestamp: new Date(msg.sentAt).getTime(),
            isOwn: msg.senderRole === 'host',
            isPrivate: true,
            channel: name,
            isWaitingRoomMessage: true,
            reactions: [],
          }));
          setMessages((prev) => {
            const existingIds = new Set(prev.map(m => m.id));
            const newMsgs = wrMessages.filter(m => !existingIds.has(m.id));
            return newMsgs.length > 0 ? [...newMsgs, ...prev] : prev;
          });
          // Remove this participant's carry-over data after loading
          delete allCarryOver[name];
          if (Object.keys(allCarryOver).length === 0) {
            sessionStorage.removeItem('waitingRoomChatCarryOver');
          } else {
            sessionStorage.setItem('waitingRoomChatCarryOver', JSON.stringify(allCarryOver));
          }
        }
      }
    } catch (e) {
      console.warn('[ChatPanel] Failed to load waiting room carry-over:', e);
    }
  };

  return (
    <div {...getRootProps()} className="flex flex-col h-full relative">
      <input {...getInputProps()} />
      {isDragActive && (
        <div className="absolute inset-0 z-50 bg-[#0a1628]/80 border-2 border-dashed border-[#6a1fbf] rounded-lg flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <Paperclip className="w-8 h-8 text-[#6a1fbf] mx-auto mb-2" />
            <p className="text-sm text-white font-medium">Drop file here</p>
            <p className="text-xs text-gray-400 mt-1">Images or documents</p>
          </div>
        </div>
      )}
      {/* AI Features Nav + Export — host/co-host only for AI Assistant and Export */}
      {(isHostOrCoHost || pinnedMessages.length > 0) && (
      <div className="px-4 pt-3 pb-2 border-b border-white/10 flex items-center justify-between">
        {isHostOrCoHost ? (
          <button
            onClick={() => setShowAIAssistant(true)}
            className="flex items-center gap-2 px-3 py-1.5 hover:bg-white/5 rounded-lg transition-colors text-xs text-gray-400 hover:text-white"
            title="AI Chat Assistant"
          >
            <Bot className="w-3.5 h-3.5" />
            <span>AI Assistant</span>
          </button>
        ) : (
          <div />
        )}
        <div className="flex items-center gap-2">
          {/* Export Button — host/co-host only */}
          {isHostOrCoHost && (
          <div className="relative" ref={exportMenuRef}>
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={messages.length === 0}
              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-colors ${messages.length === 0
                ? 'bg-white/5 text-gray-600 cursor-not-allowed'
                : showExportMenu
                  ? 'bg-[#6a1fbf]/20 text-[#6a1fbf]'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
              title="Export chat"
            >
              <Download className="w-3 h-3" />
              Export
            </button>

            {/* Export Menu Dropdown */}
            {showExportMenu && (
              <div className="absolute right-0 top-full mt-1 bg-[#1a2744] rounded-lg border border-white/10 shadow-xl z-30 min-w-[140px] overflow-hidden">
                <button
                  onClick={() => { exportChat('txt'); setShowExportMenu(false); }}
                  className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-white/10 transition-colors flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Text (.txt)
                </button>
                <button
                  onClick={() => { exportChat('csv'); setShowExportMenu(false); }}
                  className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-white/10 transition-colors flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  CSV (.csv)
                </button>
                <button
                  onClick={() => { exportChat('json'); setShowExportMenu(false); }}
                  className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-white/10 transition-colors flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  JSON (.json)
                </button>
              </div>
            )}
          </div>
          )}

          {/* Pinned count indicator */}
          {pinnedMessages.length > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs bg-yellow-500/20 text-yellow-400">
              <Pin className="w-3 h-3 fill-yellow-400" />
              {pinnedMessages.length}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Channel Navigation */}
      <div className="border-b border-white/10" style={{ padding: '12px 16px' }}>
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
          {/* Everyone Button */}
          <button
            onClick={() => setActiveChannel('Everyone')}
            className={`flex flex-col items-center justify-center w-14 h-14 rounded-lg transition-all flex-shrink-0 relative ${activeChannel === 'Everyone'
              ? 'bg-[#6a1fbf] text-white'
              : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
              }`}
          >
            <Users className="w-5 h-5 mb-0.5" />
            <span className="text-[9px] font-medium">Everyone</span>
            {getUnreadCount('Everyone') > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
                {getUnreadCount('Everyone') > 99 ? '99+' : getUnreadCount('Everyone')}
              </span>
            )}
          </button>

          {/* Separator */}
          <div className="w-px h-10 bg-white/10 flex-shrink-0"></div>

          {/* New Chat Button */}
          <div className="relative" ref={newChatRef}>
            <button
              onClick={() => setShowNewChatPopover(!showNewChatPopover)}
              className="flex flex-col items-center justify-center w-14 h-14 rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white transition-all flex-shrink-0"
            >
              <span className="text-xl leading-none mb-0.5">+</span>
              <span className="text-[9px] font-medium">New chat</span>
            </button>

            {/* New Chat Popover */}
            {showNewChatPopover && (
              <div className="fixed top-35 left-0 top-full mt-2 bg-[#1a2744] rounded-lg border border-white/10 shadow-xl z-30 w-80 overflow-hidden">
                <div className="p-2 border-b border-white/10">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by name..."
                      className="w-full bg-white/5 border border-white/10 rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-[#6a1fbf]/50"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {filteredParticipants.length > 0 ? (
                    filteredParticipants.map((participant) => (
                      <button
                        key={participant.id}
                        onClick={() => startPrivateChat(participant)}
                        className="w-full flex items-center gap-2 p-2 hover:bg-white/10 transition-colors text-left"
                      >
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-semibold text-sm flex-shrink-0"
                          style={{ backgroundColor: getColorFromName(participant.displayName || '') }}
                        >
                          {getInitials(participant.displayName || '')}
                        </div>
                        <span className="text-sm text-white truncate">{participant.displayName}</span>
                      </button>
                    ))
                  ) : (
                    <div className="p-3 text-xs text-gray-500 text-center">
                      {searchQuery ? 'No participants found' : 'No other participants'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Private Conversation Tabs */}
          {privateChannels.map((channel) => {
            if (!channel) return null;
            const unreadCount = getUnreadCount(channel);
            const firstName = (channel || '').split(' ')[0];
            return (
              <button
                key={channel}
                onClick={() => setActiveChannel(channel)}
                className={`flex flex-col items-center justify-center w-14 h-14 rounded-lg transition-all flex-shrink-0 relative ${activeChannel === channel
                  ? 'bg-blue-600 text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-blue-600/20 hover:text-white'
                  }`}
              >
                <div
                  className="w-6 h-6 rounded flex items-center justify-center text-white font-semibold text-xs mb-0.5"
                  style={{ backgroundColor: getColorFromName(channel) }}
                >
                  {getInitials(channel)}
                </div>
                <span className="text-[9px] font-medium truncate max-w-full px-1">{firstName}</span>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Pinned Messages Section - Always visible at top when there are pinned messages */}
      {pinnedMessages.length > 0 && (
        <div className="sticky top-0 z-10 border-b border-white/10 bg-[#0a1628]/95 backdrop-blur-sm p-2 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-yellow-400 font-medium flex items-center gap-1">
              <Pin className="w-3 h-3 fill-yellow-400" /> Pinned ({pinnedMessages.length})
            </span>
            {pinnedMessages.length > 1 && (
              <button
                onClick={() => setShowPinned(!showPinned)}
                className="text-xs text-gray-400 hover:text-gray-300 transition-colors"
              >
                {showPinned ? 'Show less' : 'Show all'}
              </button>
            )}
          </div>
          <div className={`space-y-1.5 ${!showPinned && pinnedMessages.length > 1 ? 'max-h-16' : 'max-h-32'} overflow-y-auto`}>
            {(showPinned ? pinnedMessages : pinnedMessages.slice(0, 1)).map(msg => (
              <div
                key={`pinned-${msg.id}`}
                className="text-xs bg-gradient-to-r from-yellow-500/10 to-transparent rounded-lg px-2.5 py-2 border-l-2 border-yellow-500/50 group"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <span className="text-yellow-400 font-medium">{msg.user}</span>
                    <p className="text-gray-300 mt-0.5 line-clamp-2">
                      {msg.message || (msg.image ? '[Image]' : '[File]')}
                    </p>
                  </div>
                  {canModerateChat && (
                    <button
                      onClick={() => togglePin(msg.id)}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-white/10 rounded transition-all flex-shrink-0"
                      title="Unpin message"
                    >
                      <X className="w-3 h-3 text-gray-400" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Date separator */}
      <div style={{ padding: '8px 16px' }}>
        <div className="text-center text-white/25" style={{ fontSize: '11px' }}>Today</div>
      </div>

      {/* Messages */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-visible relative" ref={scrollRef} onScroll={handleScroll} style={{ padding: '10px 12px' }}>
        <div className="pb-2">
          {filteredMessages.length === 0 ? (
            <div className="text-center py-8 text-white/40" style={{ fontSize: '14px' }}>
              <p>No messages yet</p>
              <p className="mt-1" style={{ fontSize: '13px' }}>
                {activeChannel === 'Everyone'
                  ? 'Be the first to say hello!'
                  : `Start a conversation with ${activeChannel}`}
              </p>
            </div>
          ) : (
            filteredMessages.map((msg, idx) => {
              const color = getColorFromName(msg.user);
              const initials = getInitials(msg.user);
              const isEditing = editingMessageId === msg.id;
              const isFeatured = featuredCommentId === msg.id;
              const prevMsg = idx > 0 ? filteredMessages[idx - 1] : null;
              const isSameUser = prevMsg && prevMsg.user === msg.user;

              return (
                <div
                  key={msg.id}
                  className={`group relative flex items-start rounded-md transition-colors hover:bg-white/[0.04] ${isFeatured ? 'bg-yellow-500/5' : ''} ${msg.isPinned ? 'bg-[#6a1fbf]/5' : ''}`}
                  style={{
                    // Tight inter-message spacing — group consecutive messages
                    // from the same user with just 1px, fresh users get 6px.
                    marginTop: idx === 0 ? 0 : isSameUser ? '1px' : '6px',
                    gap: '6px',
                    padding: '2px 6px',
                    marginLeft: '-6px',
                    marginRight: '-6px',
                    // Same-user continuations: indent text to align with the
                    // first message's content area (avatar 28px + gap 6px = 34px)
                    // instead of rendering an invisible 28px spacer div.
                    ...(isSameUser ? { paddingLeft: '40px' } : {}),
                  }}
                >
                  {/* Timestamp popover — shows on hover in the left gutter for
                      same-user continuation messages. Absolutely positioned so
                      it takes ZERO vertical space (the old inline <span> added
                      a full line of height even when opacity-0). */}
                  {isSameUser && (
                    <span
                      className="absolute left-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none text-white/30 select-none"
                      style={{ fontSize: '9px' }}
                    >
                      {msg.timestamp}
                    </span>
                  )}

                  {/* Avatar — only show for first message in a group */}
                  {!isSameUser && (
                    <div
                      className="shrink-0 flex items-center justify-center text-white font-semibold"
                      style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: color, fontSize: '11px' }}
                    >
                      {initials}
                    </div>
                  )}

                  {/* Message Content */}
                  <div className="flex-1 min-w-0">
                    {/* Name + Timestamp header — only for first message in a group */}
                    {!isSameUser && (
                      <div className="flex items-baseline gap-2" style={{ marginBottom: '1px' }}>
                        <span className="text-white font-semibold" style={{ fontSize: '13px' }}>{msg.user}</span>
                        <span className="text-white/40" style={{ fontSize: '10px' }}>{msg.timestamp}</span>
                        {msg.isPinned && <Pin className="w-3 h-3 text-[#6a1fbf]" />}
                        {isFeatured && <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />}
                      </div>
                    )}

                    {/* Editing Mode */}
                    {isEditing ? (
                      <div>
                        <input
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white mb-1.5 focus:outline-none focus:border-[#6a1fbf]"
                          style={{ fontSize: '15px' }}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSaveEdit(msg.id);
                            }
                            if (e.key === 'Escape') {
                              handleCancelEdit();
                            }
                          }}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSaveEdit(msg.id)}
                            className="px-3 py-1 bg-[#6a1fbf] hover:bg-[#7a2fcf] rounded text-xs text-white transition-colors flex items-center gap-1"
                          >
                            <Check className="w-3 h-3" /> Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="px-3 py-1 bg-white/10 hover:bg-white/20 rounded text-xs text-gray-300 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="break-words relative">
                        {/* Reply Context */}
                        {msg.replyTo && (
                          <div className="mb-1.5 px-2 py-1.5 bg-white/5 rounded border-l-2 border-[#6a1fbf]">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <CornerDownRight className="w-3 h-3 text-[#6a1fbf] flex-shrink-0" />
                              <span className="text-[#6a1fbf] font-semibold" style={{ fontSize: '11px' }}>Replying to {msg.replyTo.user}</span>
                            </div>
                            <p className="text-white/40 line-clamp-2 pl-4 italic" style={{ fontSize: '13px' }}>
                              "{msg.replyTo.message || '[Image/File]'}"
                            </p>
                          </div>
                        )}

                        {/* Image */}
                        {msg.image && (
                          <img
                            src={msg.image}
                            alt="Shared"
                            className="rounded-md mb-1.5 max-w-full max-h-40 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => window.open(msg.image, '_blank')}
                          />
                        )}

                        {/* File attachment */}
                        {msg.file && (
                          <div className="mb-1.5 bg-white/5 rounded-md p-2 flex items-center gap-2 hover:bg-white/8 transition-colors">
                            <FileText className="w-5 h-5 text-[#6a1fbf] flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-white truncate" style={{ fontSize: '13px' }}>{msg.file.name}</p>
                              <p className="text-white/30" style={{ fontSize: '11px' }}>{formatFileSize(msg.file.size)}</p>
                            </div>
                            {msg.file.url && (
                              <a
                                href={msg.file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                download={msg.file.name}
                                className="p-1.5 hover:bg-white/10 rounded-md transition-colors flex-shrink-0"
                                title="Download file"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Download className="w-4 h-4 text-[#00c853]" />
                              </a>
                            )}
                          </div>
                        )}

                        {/* Message text */}
                        {msg.message && (
                          <p className="text-white whitespace-pre-wrap break-words" style={{ fontSize: '13px', lineHeight: '1.4' }}>
                            {renderTextWithLinks(msg.message)}
                            {msg.edited && (
                              <span className="text-white/30 ml-1" style={{ fontSize: '10px' }}>(edited)</span>
                            )}
                          </p>
                        )}

                        {/* Timestamp popover for same-user continuation messages —
                            absolutely positioned so it takes zero vertical space.
                            Shows on row hover to the left of the message text. */}

                        {/* Reactions — inline with minimal spacing so they
                            don't push subsequent messages down noticeably */}
                        {msg.reactions.length > 0 && (
                          <div className="flex gap-0.5 mt-0.5 flex-wrap">
                            {msg.reactions.map((r, i) => {
                              const myUserId = String(localUserId || localParticipantId);
                              const iReacted = r.users?.includes(myUserId);
                              return (
                              <button
                                key={i}
                                onClick={() => addReaction(msg.id, r.emoji)}
                                className={`px-1 py-px rounded-full flex items-center gap-0.5 transition-colors ${iReacted ? 'bg-blue-500/20 ring-1 ring-blue-500/40 hover:bg-blue-500/30' : 'bg-white/5 hover:bg-white/10'}`}
                                style={{ fontSize: '11px', lineHeight: '1.3' }}
                              >
                                <span>{r.emoji}</span>
                                <span className="text-white/40">{r.count}</span>
                              </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                  </div>

                  {/* Floating Action Toolbar — appears top-right on row hover.
                      Contains quick reactions + the more-actions 3-dot menu in
                      a single compact bar so it doesn't push message content
                      down or eat horizontal space. Absolutely positioned so
                      it doesn't change row height.
                      pointer-events-none by default so the invisible toolbar
                      doesn't intercept text-selection drags; switched on with
                      pointer-events-auto when actually visible. Top offset
                      pulled higher (-top-7) so the visible bar sits above the
                      row instead of overlapping the message text — users were
                      unable to select text when the toolbar covered it. */}
                  {!isEditing && (
                    <div className={`opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity absolute ${idx === 0 ? 'top-0' : '-top-7'} right-1 flex items-center gap-0.5 bg-[#1a2744] border border-white/10 rounded-md shadow-lg px-1 py-0.5 z-10`}>
                      {REACTION_EMOJIS.slice(0, 5).map(emoji => (
                        <button
                          key={emoji}
                          onClick={() => addReaction(msg.id, emoji)}
                          className="hover:scale-125 transition-transform p-1 leading-none"
                          style={{ fontSize: '13px' }}
                          title={`React with ${emoji}`}
                        >
                          {emoji}
                        </button>
                      ))}
                      <div className="w-px h-4 bg-white/10 mx-0.5" />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeMenuId === msg.id) {
                            setActiveMenuId(null);
                          } else {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const menuWidth = 170;
                            let left = rect.left - menuWidth;
                            if (left < 10) {
                              left = rect.right + 4;
                            }
                            setMenuPosition({
                              top: rect.top,
                              left: left
                            });
                            setActiveMenuId(msg.id);
                          }
                        }}
                        className="p-1 hover:bg-white/10 rounded transition-all"
                        title="More actions"
                      >
                        <MoreVertical className="w-3.5 h-3.5 text-white/60" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* New messages indicator */}
        {showNewMessagesIndicator && (
          <button
            onClick={scrollToBottom}
            className="sticky bottom-2 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-[#6a1fbf] hover:bg-[#7a2fcf] text-white rounded-full shadow-lg transition-all flex items-center gap-1.5 z-10"
            style={{ fontSize: '12px' }}
          >
            <span>New messages</span>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="text-white">
              <path d="M6 2v8m0 0l3-3m-3 3L3 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
      </div>

      {/* Fixed Dropdown Menu - rendered via Portal to document.body */}
      {activeMenuId && createPortal(
        <div
          className="fixed bg-[#1a2744] rounded-lg border border-white/10 shadow-2xl w-[170px] py-1"
          style={{
            top: menuPosition.top,
            left: menuPosition.left,
            zIndex: 99999
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {(() => {
            const msg = filteredMessages.find(m => m.id === activeMenuId);
            if (!msg) return null;
            const isFeatured = featuredCommentId === msg.id;
            return (
              <>
                {/* Reply */}
                <button
                  onClick={() => { handleReply(msg); setActiveMenuId(null); }}
                  className="w-full px-2.5 py-1.5 text-left text-[11px] text-gray-300 hover:bg-white/10 transition-colors flex items-center gap-1.5"
                >
                  <Reply className="w-3 h-3" />
                  Reply
                </button>

                {/* Edit (own messages only) */}
                {msg.isOwn && (
                  <button
                    onClick={() => { handleEditMessage(msg); setActiveMenuId(null); }}
                    className="w-full px-2.5 py-1.5 text-left text-[11px] text-gray-300 hover:bg-white/10 transition-colors flex items-center gap-1.5"
                  >
                    <Edit2 className="w-3 h-3" />
                    Edit
                  </button>
                )}

                {/* Star - broadcasts to all screens (hosts/presenters only) */}
                {canModerateChat && (
                  <button
                    onClick={() => handleStarMessage(msg.id)}
                    className="w-full px-2.5 py-1.5 text-left text-[11px] text-gray-300 hover:bg-white/10 transition-colors flex items-center gap-1.5"
                  >
                    <Star className={`w-3 h-3 ${starredMessage?.id === msg.id ? 'text-yellow-400 fill-yellow-400' : ''}`} />
                    {starredMessage?.id === msg.id ? 'Unstar' : 'Star'}
                  </button>
                )}

                {/* Pin */}
                <button
                  onClick={() => togglePin(msg.id)}
                  className="w-full px-2.5 py-1.5 text-left text-[11px] text-gray-300 hover:bg-white/10 transition-colors flex items-center gap-1.5"
                >
                  <Pin className={`w-3 h-3 ${msg.isPinned ? 'text-[#6a1fbf]' : ''}`} />
                  {msg.isPinned ? 'Unpin' : 'Pin'}
                </button>

                {/* Shadow Ban (hosts/moderators only, not for own messages) */}
                {canModerateChat && !msg.isOwn && (
                  (() => {
                    const senderId = msg.participantId;
                    const senderBanned = shadowBannedParticipants?.has?.(senderId)
                      || shadowBannedParticipants?.has?.(String(senderId))
                      || (() => { const nuid = String(stringToNumericUid(senderId)); return nuid && nuid !== '0' && shadowBannedParticipants?.has?.(nuid); })();
                    return (
                      <button
                        onClick={() => { handleToggleShadowBan(msg.participantId, msg.user); setActiveMenuId(null); }}
                        className={`w-full px-2.5 py-1.5 text-left text-[11px] hover:bg-white/10 transition-colors flex items-center gap-1.5 ${senderBanned ? 'text-orange-400' : 'text-gray-300'}`}
                      >
                        {senderBanned ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {senderBanned ? 'Remove shadow ban' : 'Shadow ban'}
                      </button>
                    );
                  })()
                )}

                {/* Delete (own messages OR hosts/presenters can delete any) */}
                {(msg.isOwn || canModerateChat) && (
                  <button
                    onClick={() => deleteMessage(msg.id)}
                    className="w-full px-2.5 py-1.5 text-left text-[11px] text-red-400 hover:bg-white/10 transition-colors flex items-center gap-1.5"
                  >
                    <Trash2 className="w-3 h-3" />
                    Delete
                  </button>
                )}

                {/* Remove from meeting (hosts/moderators only, not for own messages) */}
                {canModerateChat && !msg.isOwn && (
                  <button
                    onClick={() => { handleKickParticipant(msg.participantId, msg.user); setActiveMenuId(null); }}
                    className="w-full px-2.5 py-1.5 text-left text-[11px] text-red-400 hover:bg-white/10 transition-colors flex items-center gap-1.5"
                  >
                    <UserMinus className="w-3 h-3" />
                    Remove from meeting
                  </button>
                )}
              </>
            );
          })()}
        </div>,
        document.body
      )}

      {/* Input Area — flex-shrink-0 so the image/file preview can't push the
          input row off the bottom of a constrained chat panel.  Without it,
          the parent `flex flex-col h-full` allows this section to shrink,
          and on shorter viewports the preview's added height squeezes the
          send row out of view. */}
      <div className="border-t border-white/10 flex-shrink-0" style={{ padding: '12px 16px' }}>
        {/* Reply Context - shows what you're replying to */}
        {replyingTo && (
          <div className="mb-2 bg-[#0a1628] rounded-lg p-2.5 border-l-3 border-[#6a1fbf]">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-1">
                  <CornerDownRight className="w-3.5 h-3.5 text-[#6a1fbf] flex-shrink-0" />
                  <span className="text-xs text-[#6a1fbf] font-semibold">Replying to {replyingTo.user}</span>
                </div>
                <p className="text-xs text-gray-400 truncate pl-5 italic">
                  "{replyingTo.message || '[Image/File]'}"
                </p>
              </div>
              <button
                onClick={cancelReply}
                className="p-1 hover:bg-white/10 rounded transition-colors flex-shrink-0"
              >
                <X className="w-3.5 h-3.5 text-gray-400" />
              </button>
            </div>
          </div>
        )}

        {/* Image Preview */}
        {imagePreview && (
          <div className="mb-2 relative inline-block">
            <img
              src={imagePreview}
              alt="Preview"
              className="rounded-lg object-cover"
              style={{ maxHeight: 'clamp(48px, 10vh, 128px)' }}
            />
            <button
              onClick={handleRemoveImage}
              className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 rounded-full p-1 transition-colors"
            >
              <X className="w-3 h-3 text-white" />
            </button>
          </div>
        )}

        {/* File Preview */}
        {attachedFile && (
          <div className="mb-2 bg-white/5 rounded-lg p-2 flex items-center gap-2">
            <FileText className="w-6 h-6 text-[#6a1fbf]" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate">{attachedFile.name}</p>
              <p className="text-xs text-gray-500">{formatFileSize(attachedFile.size)}</p>
            </div>
            <button
              onClick={handleRemoveFile}
              className="p-1 hover:bg-white/10 rounded transition-colors"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        )}

        <div className="relative">
          {/* Emoji Picker */}
          {showEmojiPicker && (
            <div
              ref={emojiPickerRef}
              className="absolute bottom-full left-0 mb-2 bg-[#1a2744] rounded-xl border border-white/10 shadow-xl p-3 w-64 max-h-64 overflow-y-auto z-20"
            >
              {Object.entries(EMOJI_CATEGORIES).map(([category, emojis]) => (
                <div key={category} className="mb-3">
                  <p className="text-xs text-gray-500 mb-2">{category}</p>
                  <div className="grid grid-cols-7 gap-1">
                    {emojis.map(emoji => (
                      <button
                        key={emoji}
                        onClick={() => addEmoji(emoji)}
                        className="p-1.5 hover:bg-white/10 rounded transition-colors text-lg"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Icon buttons row */}
          <div className="flex items-center gap-1 mb-0.5">
            {/* Emoji button */}
            <button
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className={`p-1.5 rounded-lg transition-colors ${showEmojiPicker ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-gray-300 hover:bg-white/5'
                }`}
              title="Emoji"
            >
              <Smile className="w-4 h-4" />
            </button>

            {/* File attachment button */}
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="p-1.5 text-gray-400 hover:text-gray-300 hover:bg-white/5 rounded-lg transition-colors disabled:opacity-50"
              title="Attach file"
            >
              <Paperclip className="w-4 h-4" />
            </button>

            {/* Image button */}
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
            />
            <button
              onClick={() => imageInputRef.current?.click()}
              disabled={isUploading}
              className="p-1.5 text-gray-400 hover:text-gray-300 hover:bg-white/5 rounded-lg transition-colors disabled:opacity-50"
              title="Upload image"
            >
              <ImageIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Typing indicator - always rendered to reserve space */}
          <div className="text-white/40 italic whitespace-nowrap overflow-hidden text-ellipsis shrink-0" style={{ height: '16px', lineHeight: '16px', fontSize: '12px', visibility: typingUsers && typingUsers.length > 0 ? 'visible' : 'hidden' }}>
            {typingUsers && typingUsers.length > 0 ? (
              <>
                {typingUsers.slice(0, 2).join(', ')}
                {typingUsers.length > 2 && ` and ${typingUsers.length - 2} others`}
                {typingUsers.length === 1 ? ' is' : ' are'} typing...
              </>
            ) : '\u00A0'}
          </div>

          {/* Input and send row */}
          <div className="flex items-center gap-2">
            {/* Text input */}
            <input
              ref={inputRef}
              type="text"
              name="rlink-chat-message"
              autoComplete="off"
              data-form-type="other"
              value={newMessage}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder={!canSendChat ? "Connecting to chat..." : activeChannel === 'Everyone' ? "Type a message..." : `Message ${activeChannel}...`}
              disabled={isUploading || !canSendChat}
              className="flex-1 min-w-0 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white placeholder:text-white/30 focus:outline-none focus:border-[#6a1fbf]/50 focus:ring-1 focus:ring-[#6a1fbf]/50 disabled:opacity-50"
              style={{ fontSize: '14px' }}
            />

            {/* Send button */}
            <button
              onClick={handleSend}
              disabled={(!newMessage.trim() && !imageFile && !attachedFile) || isUploading || !canSendChat}
              className={`p-2 rounded-xl transition-all shrink-0 ${(newMessage.trim() || imageFile || attachedFile) && !isUploading && canSendChat
                ? 'bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white hover:shadow-lg hover:shadow-[#6a1fbf]/25'
                : 'bg-white/5 text-gray-600 cursor-not-allowed'
                }`}
            >
              <Send className="w-4 h-4" />
            </button>
          </div>

          {!canSendChat && (
            <p className="text-white/20 mt-1.5 text-center" style={{ fontSize: '11px' }}>
              Connecting...
            </p>
          )}
        </div>
      </div>

      {/* AI Chat Assistant Modal */}
      <AIChatAssistant
        isOpen={showAIAssistant}
        onClose={() => setShowAIAssistant(false)}
        meetingContext={{
          meetingTitle: meetingSettings?.meetingName || meetingSettings?.roomName || meetingSettings?.roomId || 'Meeting',
          hostName: localParticipantName,
          participantCount: meetingSettings?.participantCount || undefined,
          duration: meetingStartedAt ? `${Math.round((Date.now() - new Date(meetingStartedAt).getTime()) / 60000)} minutes` : undefined,
        }}
        transcriptHistory={transcriptHistory}
        chatMessages={messages}
      />

      {/* Notification Settings Modal */}
      <NotificationSettingsModal
        isOpen={showNotificationSettings}
        onClose={() => setShowNotificationSettings(false)}
        settings={notificationSettings}
        onSave={setNotificationSettings}
      />

      {/* Starred Message Overlay - Shows on all screens for 10 seconds */}
      {starredMessage && createPortal(
        <div className="fixed inset-0 pointer-events-none z-[99998] flex items-end justify-center pb-32">
          <div className="pointer-events-auto animate-in slide-in-from-bottom-4 fade-in duration-300 max-w-lg w-full mx-4">
            <div className="bg-gradient-to-r from-[#6a1fbf] to-[#00c853] p-[2px] rounded-2xl shadow-2xl shadow-purple-500/30">
              <div className="bg-[#001233]/95 backdrop-blur-xl rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-gradient-to-r from-[#6a1fbf] to-[#00c853] flex items-center justify-center">
                    <Star className="w-5 h-5 text-white fill-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-white">{starredMessage.user}</span>
                      <span className="text-xs text-gray-400">{starredMessage.timestamp}</span>
                    </div>
                    <p className="text-white/90 text-sm leading-relaxed">
                      {starredMessage.text || starredMessage.message}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setStarredMessage(null);
                      if (starredTimeoutRef.current) {
                        clearTimeout(starredTimeoutRef.current);
                      }
                    }}
                    className="shrink-0 p-1 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
                {/* Progress bar showing time remaining */}
                <div className="mt-3 h-1 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#6a1fbf] to-[#00c853]"
                    style={{
                      animation: 'shrinkWidth 10s linear forwards',
                      width: '100%'
                    }}
                  />
                </div>
                <style>{`
                  @keyframes shrinkWidth {
                    from { width: 100%; }
                    to { width: 0%; }
                  }
                `}</style>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
